import React, { useEffect, useState, useMemo } from 'react';
import useDocumentTitle from '../../hooks/useDocumentTitle';
import useModalEscape from '../../hooks/useModalEscape';
import useModalTransition from '../../hooks/useModalTransition';
import Sidebar from '../../components/Sidebar/Sidebar';
import { useData } from '../../context/DataContext';
import { Search, ClipboardList, User, MapPin, Briefcase, MoreVertical, X, Mail, Phone, Globe, Filter, Loader2, Info } from 'lucide-static';
import { db } from '../../firebase';
import { doc, getDoc, updateDoc, collection, query, where, getDocs } from 'firebase/firestore';
import './SA_Logs.css'; 
import './SA_Requests.css';

const Icon = ({ icon, className, size = 20, color, ...props }) => {
  const getProcessedSvg = () => {
    let svgString = icon;
    if (size) svgString = svgString.replace('<svg', `<svg width="${size}" height="${size}"`);
    if (color) svgString = svgString.replace('<svg', `<svg color="${color}" stroke="${color}"`);
    if (className) svgString = svgString.replace('<svg', `<svg class="${className}"`);
    return svgString;
  };
  return <span dangerouslySetInnerHTML={{ __html: getProcessedSvg() }} {...props} />;
};

const ConfirmationModal = ({ isOpen, title, message, onConfirm, onCancel, confirmText = "Confirm", cancelText = "No", type = "primary" }) => {
  useModalEscape(onCancel, isOpen);
  if (!isOpen) return null;

  return (
    <div className="requests-modal-overlay" style={{ zIndex: 40000 }} onClick={e => e.stopPropagation()}>
      <div className="requests-modal-container" style={{ maxWidth: '400px', padding: '32px', textAlign: 'center' }} onClick={e => e.stopPropagation()}>
        <h3 className="card-title" style={{ fontSize: '20px', marginBottom: '12px' }}>{title}</h3>
        <p className="card-subtitle" style={{ fontSize: '14px', marginBottom: '24px', lineHeight: '1.5' }}>{message}</p>
        
        <div style={{ display: 'flex', gap: '12px', justifyContent: 'center' }}>
          <button className="btn-reject" onClick={e => { e.stopPropagation(); onCancel(); }} style={{ color: '#6b7280', flex: 1 }}>
            {cancelText}
          </button>
          <button 
            className="btn-approve" 
            onClick={e => { e.stopPropagation(); onConfirm(); }} 
            style={{ 
              flex: 1, 
              background: type === 'danger' ? '#ef4444' : '#31356e' 
            }}
          >
            {confirmText}
          </button>
        </div>
      </div>
    </div>
  );
};

const RequestDetailModal = ({ request: incomingRequest, onClose, onUpdateStatus, onDelete, conflict }) => {
  const [confirmConfig, setConfirmConfig] = useState(null);
  const [request, setRequest] = useState(incomingRequest);
  const shouldRender = useModalTransition(!!incomingRequest);

  useEffect(() => {
    if (incomingRequest) {
      setRequest(incomingRequest);
    }
    setConfirmConfig(null);
  }, [incomingRequest]);

  useModalEscape(onClose, !!incomingRequest);

  if (!shouldRender || !request) return null;

  const handleApproveClick = () => {
    if (request.collectionType === 'appRequests') {
      onUpdateStatus(request.id, 'resolved');
      onClose();
      return;
    }

    const message = conflict 
      ? `An admin (${conflict.name}) is already assigned to this location. Are you sure you want to assign another one?`
      : "Please ensure you have verified the requester's identity and contacted them already before approving.";

    setConfirmConfig({
      title: conflict ? "Duplicate Assignment Warning" : "Approve Request?",
      message: message,
      confirmText: conflict ? "Yes, Continue" : "Yes",
      onConfirm: () => {
        onUpdateStatus(request.id, 'approved');
        setConfirmConfig(null);
        onClose();
      }
    });
  };

  const handleRejectClick = () => {
    onUpdateStatus(request.id, 'rejected');
    onClose();
  };

  const handleUnapproveClick = () => {
    onUpdateStatus(request.id, 'pending');
    onClose();
  };

  const handleAccountCreatedClick = () => {
    const isRenewal = request.requestCategory === 'partnership_renewal' || request.isRenewal;
    setConfirmConfig({
      title: isRenewal ? "Confirm Renewal?" : "Confirm Account Creation?",
      message: isRenewal 
        ? "Are you sure you want to approve this renewal? This will extend their partnership subscription by 1 year." 
        : "Has the account for this admin already been created in User Management?",
      confirmText: isRenewal ? "Renew" : "Yes",
      onConfirm: async () => {
        if (isRenewal) {
           try {
             let userId = request.userId || request.uid;
             
             if (!userId) {
                const email = request.email || request.userEmail;
                if (email) {
                   const q = query(collection(db, 'users'), where('email', '==', email));
                   const snap = await getDocs(q);
                   if (!snap.empty) {
                      userId = snap.docs[0].id;
                   }
                }
             }

             if (!userId && conflict) {
                userId = conflict.uid || conflict.id;
             }

             if (userId) {
                const userRef = doc(db, 'users', userId);
                const userSnap = await getDoc(userRef);
                if (userSnap.exists()) {
                   const userData = userSnap.data();
                   const newEnd = new Date();
                   newEnd.setFullYear(newEnd.getFullYear() + 1);
                   await updateDoc(userRef, { subscriptionTime: newEnd.toISOString(), subscriptionStatus: 'active' });
                }
             } else {
                console.warn("Could not find user ID to renew partnership.");
             }
           } catch(e) {
             console.error("Error extending subscription:", e);
           }
        }
        onUpdateStatus(request.id, 'verified_admin');
        setConfirmConfig(null);
        onClose();
      }
    });
  };

  const handleUndoAccountCreatedClick = () => {
    onUpdateStatus(request.id, 'approved');
    onClose();
  };

  const handleDeleteClick = () => {
    setConfirmConfig({
      title: "Delete Record?",
      message: "Are you sure you want to permanently delete this request record? This action cannot be undone.",
      confirmText: "Delete",
      type: "danger",
      onConfirm: () => {
        onDelete(request.id);
        setConfirmConfig(null);
        onClose();
      }
    });
  };

  return (
    <>
      <div className={`requests-modal-overlay modal-transition-${incomingRequest ? 'enter' : 'exit'}`} onClick={onClose}>
        <div className="requests-modal-container" onClick={e => e.stopPropagation()}>
          <button onClick={onClose} className="modal-close-icon">
            <Icon icon={X} size={20} color="#6b7280" />
          </button>

          <div className="modal-header-text" style={{ marginBottom: '32px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap', marginBottom: '4px' }}>
              <h2 className="card-title" style={{ margin: 0 }}>Request Details</h2>
              {request.collectionType === 'appRequests' ? (
                <span className="severity-pill" style={{ background: '#f3f4f6', color: '#4b5563', borderColor: '#e5e7eb' }}>
                  {request.type || "App Feedback"}
                </span>
              ) : request.requestCategory === 'partnership_renewal' || request.isRenewal ? (
                <span className="severity-pill" style={{ background: '#eef2ff', color: '#4338ca', borderColor: '#c7d2fe', fontWeight: 600 }}>
                  Partnership Renewal
                </span>
              ) : (
                <span className="severity-pill" style={{ background: '#eff6ff', color: '#1d4ed8', borderColor: '#bfdbfe', fontWeight: 600 }}>
                  Partnership Request
                </span>
              )}
            </div>
            <p className="card-subtitle">Complete information submitted by the requester</p>
          </div>

          <div className="modal-info-grid">
            <div className="modal-info-column" style={request.collectionType === 'appRequests' ? { gridColumn: '1 / -1' } : {}}>
              <h4 className="modal-section-title">Personal Information</h4>
              <div className="info-box">
                <div className="info-item">
                  <Icon icon={User} size={16} className="info-item-icon" />
                  <strong>{request.name || request.userName || request.email || request.userEmail || "Anonymous User"}</strong>
                </div>
                {(request.email || request.userEmail) && (
                  <div className="info-item">
                    <Icon icon={Mail} size={16} className="info-item-icon" />
                    {request.anonymized || request.email === 'Deleted User' || request.userEmail === 'Deleted User'
                      ? 'Deleted User'
                      : (request.email || request.userEmail)}
                  </div>
                )}
                {request.phone && (
                  <div className="info-item">
                    <Icon icon={Phone} size={16} className="info-item-icon" />
                    {request.phone}
                  </div>
                )}
              </div>
            </div>

            {request.collectionType !== 'appRequests' && (
              <div className="modal-info-column">
                <h4 className="modal-section-title">Occupation / Designation</h4>
                <div className="info-box">
                  <div className="info-item">
                    <Icon icon={Briefcase} size={16} className="info-item-icon" />
                    {request.position}
                  </div>
                  <div className="info-item">
                    <Icon icon={Globe} size={16} className="info-item-icon" />
                    Access for {request.level}
                  </div>
                </div>
              </div>
            )}
          </div>

          {request.collectionType !== 'appRequests' && (
            <div style={{ marginTop: '32px' }}>
              <h4 className="modal-section-title">Location Coverage</h4>
              <div className="location-grid">
                <div className="location-tag"><strong>Region:</strong> <span>{request.region || '—'}</span></div>
                <div className="location-tag"><strong>Province:</strong> <span>{request.province || '—'}</span></div>
                <div className="location-tag"><strong>Municipality:</strong> <span>{request.municipality || '—'}</span></div>
                <div className="location-tag"><strong>Barangay:</strong> <span>{request.barangay || '—'}</span></div>
              </div>
            </div>
          )}

          <div style={{ marginTop: '32px' }}>
            <h4 className="modal-section-title">Message</h4>
            <div className="message-box">
              {request.message || request.details || "We are interested in implementing Avisa for our LGU to streamline our legal document management and community consultation process. We look forward to your response."}
            </div>
          </div>

          {conflict && request.status === 'pending' && (
            <div style={{ 
              background: '#fff5f5', 
              border: '1px solid #feb2b2', 
              borderRadius: '12px', 
              padding: '16px', 
              marginTop: '32px',
              display: 'flex',
              gap: '12px',
              alignItems: 'center'
            }}>
              <Icon icon={Info} size={20} color="#f56565" />
              <div>
                <p style={{ margin: 0, fontWeight: 600, color: '#c53030', fontSize: '14px' }}>
                  {request.level} Admin Already Assigned
                </p>
                <p style={{ margin: 0, color: '#c53030', fontSize: '13px' }}>
                  <strong>{conflict.name}</strong> is currently the registered admin for this location. 
                  You can still proceed with the assignment if multiple admins are required.
                </p>
              </div>
            </div>
          )}

          <div className="modal-actions">
            {request.collectionType === 'appRequests' ? (
              <>
                {request.status === 'pending' && (
                  <>
                    <button className="btn-reject" onClick={handleRejectClick}>Ignore / Reject</button>
                    <button className="btn-approve" onClick={handleApproveClick}>Mark Resolved</button>
                  </>
                )}
                {(request.status === 'resolved' || request.status === 'rejected') && (
                  <>
                    <button className="btn-reject" onClick={handleDeleteClick}>Delete Record</button>
                    <button className="btn-approve" onClick={handleUnapproveClick} style={{ background: '#6b7280' }}>Undo Action</button>
                  </>
                )}
              </>
            ) : (
              <>
                {request.status === 'pending' && (
                  <>
                    <button className="btn-reject" onClick={handleRejectClick}>Reject Request</button>
                    <button 
                      className="btn-approve" 
                      onClick={handleApproveClick}
                    >
                      Approve Request
                    </button>
                  </>
                )}
                {request.status === 'approved' && (
                  <>
                    <button className="btn-reject" onClick={handleUnapproveClick} style={{ color: '#6b7280' }}>
                      Undo Approval
                    </button>
                    <button className="btn-approve" onClick={handleAccountCreatedClick}>
                      {request.requestCategory === 'partnership_renewal' || request.isRenewal ? "Renew Partnership" : "Account Created"}
                    </button>
                  </>
                )}
                {request.status === 'verified_admin' && (
                  <>
                    <button className="btn-approve" disabled style={{ width: '100%', opacity: 0.5, cursor: 'default' }}>
                      Admin Active
                    </button>
                  </>
                )}
                {request.status === 'rejected' && (
                  <>
                    <button className="btn-reject" onClick={handleDeleteClick}>Delete Record</button>
                    <button className="btn-approve" onClick={handleUnapproveClick} style={{ background: '#6b7280' }}>Undo Rejection</button>
                  </>
                )}
              </>
            )}
          </div>
        </div>
      </div>

      <ConfirmationModal 
        isOpen={!!confirmConfig}
        title={confirmConfig?.title}
        message={confirmConfig?.message}
        confirmText={confirmConfig?.confirmText}
        type={confirmConfig?.type}
        onConfirm={confirmConfig?.onConfirm}
        onCancel={() => setConfirmConfig(null)}
      />
    </>
  );
};

const SA_Requests = () => {
  useDocumentTitle("Avisa Admin Dashboard");

  const { requests, requestsLoaded, users, updateRequestStatus, deleteRequest } = useData();
  const [searchTerm, setSearchTerm] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [showFilterModal, setShowFilterModal] = useState(false);
  const REQUESTS_PER_PAGE = 10;

  const [filterOptions, setFilterOptions] = useState({
    status: '',
    level: '',
    type: ''
  });
  const [pendingFilterOptions, setPendingFilterOptions] = useState({
    status: '',
    level: '',
    type: ''
  });



  const getExistingAdminConflict = (req) => {
    if (!users || users.length === 0 || !req) return null;
    
    return users.find(u => {
      // Only consider admins
      const role = String(u.role || '').toLowerCase();
      if (!role.includes('admin')) return false;
      
      const reqLevel = String(req.level || '').toLowerCase();
      const userLevel = String(u.adminaccessLevel || '').toLowerCase();
      
      let levelMatch = false;
      if (reqLevel === 'barangay' && userLevel === 'barangay') levelMatch = true;
      if (reqLevel === 'municipal' && (userLevel === 'municipality' || userLevel === 'municipal')) levelMatch = true;
      if (reqLevel === 'provincial' && (userLevel === 'province' || userLevel === 'provincial')) levelMatch = true;
      
      if (!levelMatch) return false;
      
      const uLoc = u.location || {};
      const normalize = (val) => String(val || '').trim().toLowerCase();

      if (reqLevel === 'barangay') {
        return normalize(uLoc.barangay) === normalize(req.barangay) && 
               normalize(uLoc.municipality) === normalize(req.municipality) && 
               normalize(uLoc.province) === normalize(req.province);
      }
      if (reqLevel === 'municipal') {
        return normalize(uLoc.municipality) === normalize(req.municipality) && 
               normalize(uLoc.province) === normalize(req.province);
      }
      if (reqLevel === 'provincial') {
        return normalize(uLoc.province) === normalize(req.province);
      }
      
      return false;
    });
  };

  const formatRelativeTime = (createdAt) => {
    if (!createdAt) return '—';
    
    let timeMs;
    if (typeof createdAt.toMillis === 'function') {
      timeMs = createdAt.toMillis();
    } else if (createdAt instanceof Date) {
      timeMs = createdAt.getTime();
    } else if (typeof createdAt === 'string') {
      timeMs = new Date(createdAt).getTime();
    } else if (typeof createdAt === 'number') {
      timeMs = createdAt;
    } else {
      return '—';
    }

    if (isNaN(timeMs)) return '—';

    return new Date(timeMs).toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit'
    });
  };

  const getStatusClass = (status) => {
    if (status === 'pending') return 'severity-warning';
    if (status === 'approved') return 'severity-info';
    if (status === 'rejected') return 'severity-high';
    if (status === 'verified_admin') return 'severity-info'; // Use same blue for verified
    return 'info';
  };

  const getStatusLabel = (status) => {
    if (status === 'verified_admin') return 'ADMIN';
    return status.toUpperCase();
  };

  const applyPendingFilters = () => {
    setFilterOptions(pendingFilterOptions);
    setCurrentPage(1);
    setShowFilterModal(false);
  };

  const resetFilters = () => {
    setPendingFilterOptions({ status: '', level: '', type: '' });
  };

  const filteredRequests = useMemo(() => {
    return requests.filter(req => {
      const locationStr = `${req.barangay || ''} ${req.municipality || ''} ${req.province || ''} ${req.region || ''}`;
      const isRenewal = req.collectionType !== 'appRequests' && (req.requestCategory === 'partnership_renewal' || req.isRenewal);
      const categoryLabel = req.collectionType === 'appRequests' ? (req.type || 'App Feedback') : (isRenewal ? 'Partnership Renewal' : 'Partnership Request');
      
      const matchesSearch = [req.name, req.userName, req.userEmail, req.position, locationStr, req.level, req.type, categoryLabel]
        .join(' ')
        .toLowerCase()
        .includes(searchTerm.toLowerCase());
      
      const matchesStatus = !filterOptions.status || req.status === filterOptions.status;
      const matchesLevel = !filterOptions.level || req.level === filterOptions.level;
      
      let matchesType = true;
      if (filterOptions.type === 'partnership_renewal') {
        matchesType = isRenewal;
      } else if (filterOptions.type === 'partnership_request') {
        matchesType = req.collectionType !== 'appRequests' && !isRenewal;
      } else if (filterOptions.type === 'appRequests') {
        matchesType = req.collectionType === 'appRequests';
      }
      
      return matchesSearch && matchesStatus && matchesLevel && matchesType;
    });
  }, [requests, searchTerm, filterOptions]);

  const totalPages = Math.max(1, Math.ceil(filteredRequests.length / REQUESTS_PER_PAGE));
  const paginatedRequests = filteredRequests.slice((currentPage - 1) * REQUESTS_PER_PAGE, currentPage * REQUESTS_PER_PAGE);

  return (
    <div className="dashboard-container">
      <Sidebar />

      <main className="main-content">
        <div className="logs-top-bar">
          <div className="logs-search-section">
            <div className="logs-search-wrapper">
              <Icon icon={Search} size={18} className="logs-search-icon" />
              <input
                type="text"
                placeholder="Search Requests (Name, LGU, Status...)"
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    setSearchTerm(searchInput);
                    setCurrentPage(1);
                  }
                }}
                className="logs-search-input"
              />
              <button 
                className={`doc-filter-toggle-btn ${showFilterModal ? 'active' : ''}`}
                onClick={() => setShowFilterModal(!showFilterModal)}
              >
                <Icon icon={Filter} size={18} />
              </button>

              {showFilterModal && (
                <>
                  <div className="doc-filter-modal-overlay" onClick={() => setShowFilterModal(false)} />
                  <div className="doc-filter-modal-content">
                    <div className="doc-filter-modal-header">
                      <span className="doc-filter-modal-title">Filter Requests</span>
                      <button className="doc-filter-modal-close-icon" onClick={() => setShowFilterModal(false)}>
                        <Icon icon={X} size={16} />
                      </button>
                    </div>
                    <div className="doc-filter-modal-body">
                      <div className="doc-filter-group">
                        <label className="doc-filter-label">Status</label>
                        <select 
                          className="doc-filter-select"
                          value={pendingFilterOptions.status}
                          onChange={(e) => setPendingFilterOptions({ ...pendingFilterOptions, status: e.target.value })}
                        >
                          <option value="">All Statuses</option>
                          <option value="pending">Pending</option>
                          <option value="approved">Approved</option>
                          <option value="verified_admin">Verified Admin</option>
                          <option value="rejected">Rejected</option>
                        </select>
                      </div>
                      <div className="doc-filter-group">
                        <label className="doc-filter-label">LGU Level</label>
                        <select 
                          className="doc-filter-select"
                          value={pendingFilterOptions.level}
                          onChange={(e) => setPendingFilterOptions({ ...pendingFilterOptions, level: e.target.value })}
                        >
                          <option value="">All Levels</option>
                          <option value="Provincial">Provincial</option>
                          <option value="Municipal">Municipal</option>
                          <option value="Barangay">Barangay</option>
                        </select>
                      </div>
                      <div className="doc-filter-group">
                        <label className="doc-filter-label">Request Type</label>
                        <select
                          className="doc-filter-select"
                          value={pendingFilterOptions.type}
                          onChange={(e) => setPendingFilterOptions({ ...pendingFilterOptions, type: e.target.value })}
                        >
                          <option value="">All Types</option>
                          <option value="partnership_request">Partnership Request</option>
                          <option value="partnership_renewal">Partnership Renewal</option>
                          <option value="appRequests">App Feedback</option>
                        </select>
                      </div>
                    </div>
                    <div className="doc-filter-modal-footer">
                      <button className="doc-filter-reset-btn" onClick={resetFilters}>Reset</button>
                      <button className="doc-filter-apply-btn" onClick={applyPendingFilters}>Apply</button>
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>

        <div className="logs-card">
          <h1 className="card-title">Access & Feedback Requests</h1>
          <p className="card-subtitle">
            Review and manage onboarding requests and app feedback
            {requestsLoaded && requests.length > 0 && ` (${requests.length} total)`}
          </p>

          <div className="logs-list">
            {!requestsLoaded ? (
              <div className="no-logs" style={{ display: 'flex', flexDirection: 'column', gap: '12px', alignItems: 'center', padding: '40px' }}>
                <Icon icon={Loader2} className="spinner" size={32} color="#31356e" />
                <span>Loading requests...</span>
              </div>
            ) : filteredRequests.length > 0 ? (
              paginatedRequests.map((req) => {
                const isRenewal = req.collectionType !== 'appRequests' && (req.requestCategory === 'partnership_renewal' || req.isRenewal);
                return (
                <div key={req.id} className="log-card">
                  <div className="log-left">
                    <Icon icon={req.collectionType === 'appRequests' ? Mail : ClipboardList} size={24} className="log-icon" />
                    <div className="log-text">
                      <div className="log-title">
                        {req.anonymized || req.email === 'Deleted User' || req.userEmail === 'Deleted User'
                          ? "Deleted User"
                          : (req.name || req.userName || req.email || req.userEmail || "Anonymous")}
                        <span className={`severity-pill ${getStatusClass(req.status)}`}>
                          {getStatusLabel(req.status)}
                        </span>
                        {req.collectionType === 'appRequests' ? (
                          <span className="severity-pill" style={{ marginLeft: '8px', background: '#f3f4f6', color: '#4b5563', borderColor: '#e5e7eb' }}>
                            {req.type || "App Feedback"}
                          </span>
                        ) : isRenewal ? (
                          <span className="severity-pill" style={{ marginLeft: '8px', background: '#eef2ff', color: '#4338ca', borderColor: '#c7d2fe', fontWeight: 600 }}>
                            Partnership Renewal
                          </span>
                        ) : (
                          <span className="severity-pill" style={{ marginLeft: '8px', background: '#eff6ff', color: '#1d4ed8', borderColor: '#bfdbfe', fontWeight: 600 }}>
                            Partnership Request
                          </span>
                        )}
                        {req.collectionType !== 'appRequests' && req.status === 'pending' && getExistingAdminConflict(req) && (
                          <span className="severity-pill severity-high" style={{ marginLeft: '8px', background: '#fef2f2', color: '#ef4444', borderColor: '#fee2e2' }}>
                            {req.level} Admin Assigned
                          </span>
                        )}
                      </div>
                      <div className="log-meta">
                        {req.collectionType === 'appRequests' ? (
                          <span style={{ display: 'flex', alignItems: 'center', gap: '4px', maxWidth: '400px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            <Icon icon={Mail} size={14} /> {req.message || req.details || "No message"}
                          </span>
                        ) : (
                          <>
                            <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                              <Icon icon={Briefcase} size={14} /> {req.position}
                            </span>
                            <span style={{ color: '#e5e7eb' }}>|</span>
                            <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                              <Icon icon={MapPin} size={14} /> {req.municipality || req.province || req.region}{req.province ? `, ${req.province}` : ''} ({req.level})
                            </span>
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                    <div className="log-time">{formatRelativeTime(req.createdAt)}</div>
                    <button 
                      onClick={() => setSelectedRequest(req)}
                      style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '4px' }}
                    >
                      <Icon icon={MoreVertical} size={20} color="#9ca3af" />
                    </button>
                  </div>
                </div>
              );
              })
            ) : (
              <div className="no-requests">
                <Icon icon={ClipboardList} size={48} className="no-requests-icon" />
                <span>
                  {searchTerm || filterOptions.status || filterOptions.level 
                    ? "No requests found matching your filters"
                    : "No requests found"}
                </span>
              </div>
            )}
          </div>

          {filteredRequests.length > REQUESTS_PER_PAGE && (
            <div className="logs-pagination">
              <span className="logs-pagination-info">Page {currentPage} of {totalPages}</span>
              <div className="logs-pagination-actions">
                <button 
                  className="logs-pagination-btn" 
                  onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                  disabled={currentPage === 1}
                >Previous</button>
                {(() => {
                  const maxVisiblePages = 20;
                  let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
                  let endPage = startPage + maxVisiblePages - 1;

                  if (endPage > totalPages) {
                    endPage = totalPages;
                    startPage = Math.max(1, endPage - maxVisiblePages + 1);
                  }

                  const pages = [];
                  for (let i = startPage; i <= endPage; i++) {
                    pages.push(i);
                  }

                  return pages.map((page) => (
                    <button
                      key={page}
                      className={`logs-pagination-btn ${currentPage === page ? "active" : ""}`}
                      onClick={() => setCurrentPage(page)}
                    >
                      {page}
                    </button>
                  ));
                })()}
                <button 
                  className="logs-pagination-btn" 
                  onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                  disabled={currentPage === totalPages}
                >Next</button>
              </div>
            </div>
          )}
        </div>
      </main>

        <RequestDetailModal 
          request={selectedRequest} 
          onClose={() => setSelectedRequest(null)} 
          onUpdateStatus={updateRequestStatus}
          onDelete={deleteRequest}
          conflict={getExistingAdminConflict(selectedRequest)}
        />
    </div>
  );
};

export default SA_Requests;
